import {
  LEVELS, LEVEL_COUNT, completedCount, isCompleted, loadProgress, saveProgress,
  unlockedCount,
} from './levels.js';
import type { Level, Progress } from './levels.js';

// Cells run through the spectrum from the first level to the last, so the
// ladder reads as one long gradient — and a level always keeps its own hue.
const hueOf = (n: number): number => ((n - 1) / LEVEL_COUNT) * 320;

// Newly opened cells light up one after another rather than all at once.
const UNLOCK_STAGGER_MS = 130;

export interface LevelSelect {
  /** Re-read progress, redraw, and announce anything newly unlocked. */
  refresh(): void;
}

export function initLevelSelect(onPick: (level: Level) => void): LevelSelect {
  const grid     = document.getElementById('level-grid')!;
  const countEl  = document.getElementById('levels-count')!;
  const cells: HTMLButtonElement[] = [];

  for (const level of LEVELS) {
    const cell = document.createElement('button');
    cell.type = 'button';
    cell.className = 'level-cell';
    cell.style.setProperty('--h', String(hueOf(level.n)));
    cell.innerHTML =
      `<span class="level-num">${level.n}</span>` +
      `<i class="level-lock fa-solid fa-lock"></i>`;
    cell.addEventListener('click', () => {
      // A locked cell is inert rather than absent — the number still tells the
      // player what's coming.
      if (cell.classList.contains('locked')) return;
      onPick(level);
    });
    grid.appendChild(cell);
    cells.push(cell);
  }

  function refresh(): void {
    const progress: Progress = loadProgress();
    const done   = completedCount(progress);
    const open   = unlockedCount(done);
    // Everything past what the player last saw open is news worth animating.
    const fresh  = Math.max(progress.seen, 0);
    let announced = 0;

    countEl.textContent = `${done}/${LEVEL_COUNT}`;

    // The next one to play is the lowest open level still unsolved.
    const next = LEVELS.find(l => l.n <= open && !isCompleted(progress, l.n))?.n ?? -1;

    cells.forEach((cell, i) => {
      const n        = i + 1;
      const unlocked = n <= open;
      const solved   = isCompleted(progress, n);

      cell.classList.toggle('locked', !unlocked);
      cell.classList.toggle('open',   unlocked && !solved);
      cell.classList.toggle('done',   solved);
      cell.classList.toggle('next',   n === next);
      cell.disabled = !unlocked;

      const best = progress.best[n];
      cell.title = unlocked
        ? (solved ? `Level ${n} — best ${best} moves` : `Level ${n}`)
        : `Level ${n} — locked`;

      cell.classList.remove('unlocking');
      if (unlocked && n > fresh) {
        // Restart the animation even if the class was only just removed.
        void cell.offsetWidth;
        cell.style.animationDelay = `${announced * UNLOCK_STAGGER_MS}ms`;
        cell.classList.add('unlocking');
        // Dropped again once it's played out, so the cell can go back to
        // whatever it normally looks like — the "next up" pulse, in
        // particular, is an animation this one would otherwise sit on top of.
        cell.addEventListener('animationend', e => {
          if (e.target === cell) cell.classList.remove('unlocking');
        }, { once: true });
        announced++;
      }
    });

    if (open > progress.seen) {
      progress.seen = open;
      saveProgress(progress);
    }
  }

  return { refresh };
}
